import React, { useEffect, useState } from 'react';
import { AutoAwesome as AssistantIcon } from '@mui/icons-material';
import { Avatar, Box, keyframes, Paper, Stack, Typography, useTheme } from '@mui/material';

import type { EmptyStateProps } from './EmptyState.types';

/**
 * Keyframe animation for highlighting examples
 * Subtle pulse effect to draw attention during onboarding
 */
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(103, 58, 183, 0);
  }
  50% {
    box-shadow: 0 0 20px 4px rgba(103, 58, 183, 0.3);
  }
`;

/**
 * Empty state component for chat interfaces
 * Displays when there are no messages yet
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="AI Assistant Ready"
 *   description="I'm here to help you edit and improve your document."
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  avatarColor,
  avatarGradient,
  className,
  examples,
  onExampleClick,
  highlightExamples = false,
}) => {
  const theme = useTheme();

  // State for cycling through highlighted examples
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState<number>(0);

  // Cycle through examples when highlighting is enabled
  useEffect(() => {
    // Only run cycling if highlighting is enabled and we have examples
    if (!highlightExamples || !Array.isArray(examples) || examples.length === 0) {
      return;
    }

    // Set up interval to cycle through examples every 3 seconds
    const interval = setInterval(() => {
      setCurrentHighlightIndex((prevIndex) => (prevIndex + 1) % examples.length);
    }, 3000);

    // Cleanup on unmount or when dependencies change
    return () => clearInterval(interval);
  }, [highlightExamples, examples]);

  // Default icon
  const displayIcon = icon || <AssistantIcon sx={{ fontSize: 28, color: 'white' }} />;

  // Determine avatar background style
  const getAvatarBackgroundStyle = () => {
    if (avatarGradient) {
      const angle = avatarGradient.angle ?? 135;
      return {
        bgcolor: 'transparent',
        background: `linear-gradient(${angle}deg, ${avatarGradient.start} 0%, ${avatarGradient.end} 100%)`,
      };
    }

    if (avatarColor) {
      return { bgcolor: avatarColor };
    }

    // Default gradient from theme
    return {
      bgcolor: 'transparent',
      background: theme.palette.chat.gradients.ai,
    };
  };

  return (
    <Box
      className={className}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 3,
        animation: 'fadeIn 0.5s ease-in',
      }}
    >
      <Avatar
        sx={{
          width: 48,
          height: 48,
          mb: 2,
          ...getAvatarBackgroundStyle(),
        }}
      >
        {displayIcon}
      </Avatar>
      <Typography
        variant="h6"
        sx={{
          color: theme.palette.chat.colors.assistantText,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.chat.colors.timestampText,
          maxWidth: 280,
          lineHeight: 1.6,
          mb: Array.isArray(examples) && examples.length > 0 ? 2 : 0,
        }}
      >
        {description}
      </Typography>

      {/* Examples section */}
      {Array.isArray(examples) && examples.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%',
            maxWidth: 320,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.chat.colors.timestampText,
              fontWeight: 500,
              fontSize: 12,
              mb: 0.5,
            }}
          >
            Examples:
          </Typography>
          <Stack spacing={1} sx={{ width: '100%', alignItems: 'flex-start' }}>
            {examples.map((example, index) => {
              const isClickable = typeof onExampleClick === 'function';
              const isCurrentlyHighlighted = highlightExamples && index === currentHighlightIndex;
              const handleClick = isClickable
                ? () => {
                    if (onExampleClick) {

                      onExampleClick(example);
                    }
                  }
                : undefined;

              return (
                <Paper
                  key={index}
                  component={isClickable ? 'button' : 'div'}
                  onClick={handleClick}
                  elevation={isCurrentlyHighlighted ? 2 : 0}
                  sx={{
                    p: 1.5,
                    width: '100%',
                    bgcolor: isCurrentlyHighlighted
                      ? theme.palette.chat.colors.userBubble
                      : 'transparent',
                    border: isCurrentlyHighlighted ? 'none' : `1px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 0,
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    ...(isCurrentlyHighlighted && {
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      animation: `${pulseGlow} 2s ease-in-out infinite`,
                    }),
                    ...(!isCurrentlyHighlighted &&
                      isClickable && {
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: theme.palette.action.hover,
                        },
                      }),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.5,
                      fontSize: '0.875rem',
                      color: isCurrentlyHighlighted
                        ? theme.palette.chat.colors.userText
                        : theme.palette.text.secondary,
                      fontStyle: 'italic',
                    }}
                  >
                    {example}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
